import { useCallback, useEffect, useState } from "react"
import { createPortal } from "react-dom"
import styled from "styled-components"

import { useLazyGetOrderDevicesQuery } from "store/api/ordersApi"
import { useLazyGetVendorOrderDevicesQuery } from "store/api/vendorApi"
import { useLazyGetWHManagerOrderDevicesQuery } from "store/api/warehouseManagerApi"
import { ApiTypes } from "types"
import { ReactComponent as ExportIcon } from "assets/svg/export-icon.svg"
import { ReactComponent as ThermostatIcon } from "assets/svg/thermostat.svg"

import downloadFile from "utils/downloadFile"
import { ModalLayout } from "layouts/ModalLayout"
import { ModalHeader } from "components/common/ModalHeader"
import { Spinner } from "components/common/Spinner"
import { ButtonNew } from "components/form/ButtonNew"

import { OrderItemRow } from "./OrderItemRow"

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 1px solid var(--border-default);

  svg {
    width: 24px;
    height: 24px;
  }
`

const ContentWrapper = styled.div`
  padding-bottom: 20px;
`

const DevicesInfo = styled.div`
  padding: 9px 16px;
  background-color: var(--surface-2);
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 12px;
  color: var(--text-muted);
  font-weight: 400;
`

const Table = styled.div`
  width: 100%;
  padding: 0 30px;
`

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 16px;
  padding: 12px 0;
  font-weight: 300;
  font-size: 14px;
  color: var(--text-muted);
`

const TableBody = styled.div`
  max-height: calc(100vh - 350px);
  overflow-y: auto;
`

const TableCell = styled.div`
  display: flex;
  align-items: center;
`

const ExportButton = styled(ButtonNew)`
  display: flex;
  align-items: center;
  gap: 0px;
  flex: 1;
`

const ExportIconStyled = styled(ExportIcon)`
  flex-shrink: 0;
  width: 16px;
  height: 16px;

  path {
    stroke: currentColor;
  }
`

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
`

interface Props {
  orderNumber: number
  isVendor: boolean
  isWHManager: boolean
  isPackedStatus: boolean
  quantity: number
  productType?: ApiTypes.Model.OrderProductType
  onClose: () => void
}

export const OrderItemsModal = ({
  orderNumber,
  isVendor,
  isWHManager,
  isPackedStatus,
  quantity,
  productType,
  onClose,
}: Props) => {
  const getQuery = useCallback(() => {
    if (isVendor) {
      return useLazyGetVendorOrderDevicesQuery()
    }
    if (isWHManager) {
      return useLazyGetWHManagerOrderDevicesQuery()
    }
    return useLazyGetOrderDevicesQuery()
  }, [isVendor, isWHManager])

  const [triggerGetOrderDevices, { data, isLoading }] = getQuery()
  const [deviceSerialNumbers, setDeviceSerialNumbers] = useState<string[]>([])

  const canReplace = isPackedStatus && isWHManager

  useEffect(() => {
    triggerGetOrderDevices(orderNumber)
  }, [orderNumber, triggerGetOrderDevices, getQuery])

  useEffect(() => {
    if (data?.data) {
      setDeviceSerialNumbers(data.data)
    }
  }, [data])

  const handleReplaceSuccess = (oldSN: string, newSN: string) => {
    setDeviceSerialNumbers(prev => prev.map(sn => (sn === oldSN ? newSN : sn)))
  }

  const handleExport = () => {
    const numberOfDevices = deviceSerialNumbers.length
    const headerRow = ["Number", "Device S/N"]
    const dataRows = deviceSerialNumbers.map((sn, index) => [index + 1, sn])

    const csvContent = [
      `Devices: ${numberOfDevices}`,
      headerRow.map(item => (item?.toString().includes(",") ? `"${item}"` : item)).join(","),
      ...dataRows.map(row =>
        row
          .map(item => (typeof item === "string" ? item.replaceAll('"', '""') : item))
          .map(item => (item?.toString().includes(",") ? `"${item}"` : item))
          .join(","),
      ),
    ].join("\n")

    downloadFile([csvContent], `order-${orderNumber}-items.csv`)
  }

  return (
    <>
      {createPortal(
        <ModalLayout
          width="600px"
          onCancel={onClose}
          cancelButtonText="Close"
          customButtons={
            <ExportButton onClick={handleExport} color="blue">
              <ExportIconStyled />
              Export Data
            </ExportButton>
          }
        >
          <ModalHeader
            title={`Items in the order ${orderNumber}`}
            subtitle="View and export the data of the items included in the order."
          >
            <IconWrapper>
              <ThermostatIcon />
            </IconWrapper>
          </ModalHeader>
          <ContentWrapper>
            {isLoading ? (
              <LoadingWrapper>
                <Spinner />
              </LoadingWrapper>
            ) : (
              <>
                <DevicesInfo>DEVICES: {quantity}</DevicesInfo>
                <Table>
                  <TableHeader>
                    <TableCell>Number</TableCell>
                    <TableCell>Device S/N</TableCell>
                  </TableHeader>
                  <TableBody>
                    {deviceSerialNumbers.map((serialNumber, index) => (
                      <OrderItemRow
                        key={index}
                        index={index}
                        serialNumber={serialNumber}
                        canReplace={canReplace}
                        orderNumber={orderNumber}
                        productType={productType}
                        onReplaceSuccess={handleReplaceSuccess}
                      />
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </ContentWrapper>
        </ModalLayout>,
        document.body,
      )}
    </>
  )
}
